import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { publicApi } from "../lib/api";
import Seo from "../components/Seo";

const LAST_UPDATED = "25 July 2026";

const H = ({ children }) => <h2 className="font-display font-extrabold text-navy text-xl md:text-2xl mt-10 mb-3">{children}</h2>;
const P = ({ children }) => <p className="text-slate-600 leading-relaxed mb-4">{children}</p>;

export default function Terms() {
  const [footer, setFooter] = useState(null);
  useEffect(() => { publicApi.footer().then(setFooter).catch(() => {}); }, []);

  const ch = footer?.companies_house || {};
  const companyName = ch.company_name || "Cloud Accounting Support Services Ltd";
  const email = footer?.contact?.email || "info@cass-online.co.uk";

  return (
    <div data-testid="terms-page">
      <Seo title="Terms & Conditions" description="The terms governing your use of the Cloud Accounting Support Services website." />

      <section className="bg-navy-deep text-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5"><FileText size={14} className="text-orange" /> Website terms</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl mb-4">Terms &amp; Conditions</h1>
          <p className="text-slate-300">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <P>These Terms &amp; Conditions govern your use of the website operated by {companyName} ("we", "us" or "our"). By accessing or using our website, you agree to be bound by these terms. If you do not agree, please do not use our website.</P>

          <H>1. Use of our website</H>
          <P>You may use our website for lawful purposes only. You must not use it in any way that breaches applicable laws or regulations, is fraudulent or harmful, or that damages, disables or impairs the website or interferes with any other party's use of it. You must not attempt to gain unauthorised access to our website, the server on which it is stored, or any connected server, computer or database.</P>

          <H>2. No professional advice</H>
          <P>The content on our website, including blog articles, guides, the Business Growth Health Check and responses from our "Cassie" website assistant, is provided for general information only. It does not constitute accounting, tax, legal, financial or other professional advice, and should not be relied upon as such. You should obtain specific professional advice tailored to your circumstances before taking, or refraining from, any action. Any engagement of our professional services is governed by a separate letter of engagement.</P>

          <H>3. Accuracy of information</H>
          <P>We take reasonable care to ensure the information on our website is accurate and up to date, but we make no warranties or guarantees as to its completeness, accuracy or currency. Tax rates, allowances, regulations and other information change over time and may become out of date.</P>

          <H>4. Intellectual property</H>
          <P>All content on this website — including text, graphics, logos, images, video, podcast material and the overall design — is owned by or licensed to {companyName} and is protected by copyright and other intellectual property laws. You may view and print content for your own personal, non-commercial use. You must not otherwise reproduce, distribute, modify or exploit any content without our prior written permission.</P>

          <H>5. Links to other websites</H>
          <P>Our website may contain links to third-party websites and resources. These links are provided for your convenience only. We have no control over, and accept no responsibility for, the content, products or services of any third-party site.</P>

          <H>6. Limitation of liability</H>
          <P>To the fullest extent permitted by law, we exclude all liability for any loss or damage arising out of or in connection with your use of, or inability to use, our website, or reliance on any content on it. Nothing in these terms excludes or limits our liability for death or personal injury caused by our negligence, for fraud, or for any other liability that cannot be excluded or limited under English law.</P>

          <H>7. Availability</H>
          <P>We do not guarantee that our website, or any content on it, will always be available or uninterrupted. We may suspend, withdraw or restrict the availability of all or any part of our website for business and operational reasons.</P>

          <H>8. Privacy</H>
          <P>Your use of our website is also governed by our <Link to="/privacy-policy" className="text-orange font-semibold hover:underline" data-testid="terms-to-privacy">Privacy Policy</Link>, which explains how we collect and use your personal data.</P>

          <H>9. Governing law</H>
          <P>These terms, their subject matter and formation, are governed by the laws of England and Wales. The courts of England and Wales will have exclusive jurisdiction over any dispute arising from or related to your use of our website.</P>

          <H>10. Changes to these terms</H>
          <P>We may revise these terms at any time by updating this page. Please check this page from time to time to take notice of any changes, as they are binding on you.</P>

          <H>11. Contact</H>
          <P>If you have any questions about these terms, please contact us at <a href={`mailto:${email}`} className="text-orange font-semibold hover:underline">{email}</a>.</P>
        </div>
      </section>
    </div>
  );
}
